import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type CourseDetail, type LessonSummary } from '../../lib/api.js'
import { useLocale } from '../../i18n/index.js'
import { Column, Main } from '../../classroom/Shell.js'
import { Bar, ButtonLink, ErrorLine, RowText, Rows, Skeleton, Title } from '../../classroom/ui.js'
import { Icon } from '../../classroom/icons.js'
import { studentCopy } from './copy.js'
import { shellCopy, type ShellStrings } from './copyShell.js'

/** "Ja i ludzie wokół mnie (A0)" → "Ja i ludzie wokół mnie": the level is on the course already. */
const moduleTitle = (t: string) => t.replace(/\s*\([^)]*\)\s*$/, '')

/**
 * The course map (Course.png): the blocks as chapters, lessons as rows, and
 * „Teraz” on the lesson to do next. Nothing is locked — adults who paid for
 * a course do not take kindly to locks — so every row opens; the current one
 * is simply the obvious place to click.
 */
export function CoursePage() {
  const { slug = '' } = useParams()
  const locale = useLocale()
  const t = studentCopy(locale)
  const s = shellCopy(locale)
  const c = s.course
  const { data, isPending, isError } = useQuery({
    queryKey: ['course', slug],
    queryFn: () => api.course.detail(slug),
  })

  const done = data?.lessons.filter((l) => l.completed).length ?? 0
  const next = data?.next ?? null

  return (
    <Column
      top={
        <nav aria-label="breadcrumb" className="flex min-w-0 items-center gap-2 text-class-body">
          <Link to="/panel/kurs" className="cl-link-quiet !font-normal">
            {c.myCourses}
          </Link>
          <span aria-hidden className="text-class-ink-faint">
            ›
          </span>
          <span className="truncate font-semibold">{data?.title ?? '…'}</span>
        </nav>
      }
      bottom={
        data && (
          <>
            <span className="hidden truncate text-class-gloss text-class-ink-soft desk:inline">
              {next ? c.next(next.number, titleFor(next, locale)) : c.finished}
            </span>
            {next && (
              <ButtonLink to={`/panel/kurs/${data.slug}/${next.slug}`} className="ml-auto max-desk:w-full">
                {next.started || done > 0 ? c.continue : c.start}
                <Icon name="arrow" size={18} stroke={2.2} />
              </ButtonLink>
            )}
          </>
        )
      }
    >
      <Main width={720} className="desk:!gap-[52px]">
        {isPending ? (
          <Skeleton lines={6} />
        ) : isError || !data ? (
          <ErrorLine>{t.errors.load}</ErrorLine>
        ) : (
          <>
            <section className="flex flex-col gap-3.5">
              <Title sub={[data.level, c.lessonsCount(data.lessons.length), data.subtitle].filter(Boolean).join(' · ')}>
                {data.title}
              </Title>
              {!data.whole && (
                <p className="m-0 text-class-gloss text-class-ink-soft">
                  {shellCopy(locale).plan.partOfCourse}{' '}
                  <Link to="/panel/lekcje" className="font-semibold whitespace-nowrap">
                    {shellCopy(locale).plan.title} ›
                  </Link>
                </p>
              )}
              <div className="mt-2 flex items-center gap-3.5">
                <Bar value={data.lessons.length ? (done / data.lessons.length) * 100 : 0} className="flex-1" />
                <span className="text-class-gloss text-class-ink-faint">{c.progress(done, data.lessons.length)}</span>
              </div>
            </section>

            <Blocks data={data} c={c} locale={locale} />

            {/* ── the glossary and the PDFs ───────────────────────────── */}
            <section className="flex flex-col">
              <Rows>
                <Link
                  to={`/panel/slownik?kurs=${data.slug}`}
                  className="flex min-h-[60px] items-center gap-4 px-3 !text-class-ink hover:bg-class-field-soft/60"
                >
                  <Icon name="search" className="shrink-0 text-class-ink-soft" />
                  <RowText title={c.glossary} meta={c.glossaryHint} />
                  <span className="font-semibold text-class-sky-700">›</span>
                </Link>
                {data.materials.map((m) => {
                  const lesson = data.lessons.find((l) => l.id === m.lessonId)
                  return (
                    <a
                      key={m.id}
                      href={`/api/course/media/${m.id}`}
                      className="flex min-h-[60px] items-center gap-4 px-3 !text-class-ink hover:bg-class-field-soft/60"
                    >
                      <Icon name="download" className="shrink-0 text-class-ink-soft" />
                      <RowText
                        title={m.filename || 'PDF'}
                        meta={[lesson ? c.lesson(lesson.number) : null, `${Math.round(m.sizeBytes / 1024)} KB`].filter(Boolean).join(' · ')}
                      />
                      <span className="whitespace-nowrap font-semibold text-class-sky-700">{c.download} ›</span>
                    </a>
                  )
                })}
              </Rows>
            </section>
          </>
        )}
      </Main>
    </Column>
  )
}

function Blocks({ data, c, locale }: { data: CourseDetail; c: ShellStrings['course']; locale: string }) {
  const nextId = data.next?.id ?? null
  const currentModule = data.lessons.find((l) => l.id === nextId)?.moduleId ?? null
  const currentIndex = data.modules.findIndex((m) => m.id === currentModule)

  // Lessons without a module (a course with no blocks) form one unnamed group.
  const groups: { id: string; title: string | null; lessons: LessonSummary[] }[] =
    data.modules.length === 0
      ? [{ id: 'all', title: null, lessons: data.lessons }]
      : data.modules
          .map((m, i) => ({ id: m.id, title: c.block(i + 1, moduleTitle(m.title)), lessons: data.lessons.filter((l) => l.moduleId === m.id) }))
          .filter((g) => g.lessons.length > 0)

  return (
    <>
      {groups.map((g) => {
        // Blocks after the current one are a quieter, more compact list.
        const later = currentIndex >= 0 && data.modules.findIndex((m) => m.id === g.id) > currentIndex
        return (
          <section key={g.id} className="flex flex-col gap-2">
            {g.title && <h2 className="cl-subheading m-0 mb-2">{g.title}</h2>}
            <ol className="m-0 list-none p-0">
              {g.lessons.map((l, i) => (
                <li key={l.id} className={i > 0 ? 'border-t border-class-line' : ''}>
                  <LessonRow l={l} slug={data.slug} current={l.id === nextId} compact={later} c={c} locale={locale} />
                </li>
              ))}
            </ol>
          </section>
        )
      })}
    </>
  )
}

function LessonRow({
  l,
  slug,
  current,
  compact,
  c,
  locale,
}: {
  l: LessonSummary
  slug: string
  current: boolean
  compact: boolean
  c: ShellStrings['course']
  locale: string
}) {
  const title = titleFor(l, locale)
  return (
    <Link
      to={`/panel/kurs/${slug}/${l.slug}`}
      aria-current={current ? 'step' : undefined}
      className={[
        'flex items-center gap-4 rounded-class-btn px-3 !text-class-ink',
        current ? 'min-h-16 bg-class-field-soft' : compact ? 'min-h-[52px] hover:bg-class-field-soft/60' : 'min-h-[60px] hover:bg-class-field-soft/60',
      ].join(' ')}
    >
      {l.completed ? (
        <span
          aria-label={c.done}
          className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-class-gloss text-class-ink-faint shadow-[inset_0_0_0_1px_var(--color-class-sky-200)]"
        >
          ✓
        </span>
      ) : current ? (
        <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-class-sky-700 text-class-gloss font-semibold text-class-white">
          {l.number}
        </span>
      ) : compact ? (
        <span className="w-[30px] shrink-0 text-center text-class-gloss text-class-ink-faint">{l.number}</span>
      ) : (
        <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-class-field-soft text-class-gloss text-class-ink-faint">
          {l.number}
        </span>
      )}
      <span className="min-w-0 flex-1 leading-snug">
        <span lang="pl" className={['block truncate', current ? 'font-semibold' : '', l.completed || compact ? 'text-class-ink-soft' : ''].join(' ')}>
          {l.title}
        </span>
        {!compact && title !== l.title && <span className="block truncate text-class-label text-class-ink-faint">{title}</span>}
      </span>
      {!compact && (
        <span className={['shrink-0 text-class-label', current ? 'font-semibold text-class-sky-700' : 'text-class-ink-faint'].join(' ')}>
          {current ? c.now : c.lesson(l.number)}
        </span>
      )}
    </Link>
  )
}

/**
 * „Moje kursy”: the list of the student's courses. With one course there is
 * nothing to choose, so it opens that course's map straight away.
 */
export function MyCoursesPage() {
  const locale = useLocale()
  const t = studentCopy(locale)
  const s = shellCopy(locale)
  const { data, isPending, isError } = useQuery({ queryKey: ['course', 'mine'], queryFn: api.course.mine })

  if (data && data.length === 1) return <Navigate to={`/panel/kurs/${data[0]!.slug}`} replace />

  return (
    <Column top={<p className="m-0 font-semibold">{s.course.myCourses}</p>}>
      <Main width={720}>
        <Title>{s.course.myCourses}</Title>
        {isPending ? (
          <Skeleton />
        ) : isError ? (
          <ErrorLine>{t.errors.load}</ErrorLine>
        ) : data.length === 0 ? (
          <div>
            <p className="m-0 font-semibold">{s.home.noCourses}</p>
            <p className="m-0 text-class-gloss text-class-ink-faint">{s.home.noCoursesHint}</p>
          </div>
        ) : (
          <Rows>
            {data.map((course) => (
              <Link
                key={course.id}
                to={`/panel/kurs/${course.slug}`}
                className="flex min-h-[72px] items-center gap-6 px-1 py-3 !text-class-ink hover:bg-class-field-soft/50"
              >
                <RowText
                  title={course.title}
                  meta={course.completed > 0 ? s.home.ofLessons(course.completed, course.lessons) : s.home.notStarted(course.lessons)}
                />
                <Bar value={course.lessons ? (course.completed / course.lessons) * 100 : 0} className="w-24 shrink-0 desk:w-[140px]" />
                <span className="hidden whitespace-nowrap font-semibold text-class-sky-700 desk:inline">{s.course.continue} ›</span>
              </Link>
            ))}
          </Rows>
        )}
      </Main>
    </Column>
  )
}

/** The title in the student's language when the file carried one. */
export function titleFor(l: { title: string; titleUk?: string; titleRu?: string }, locale: string): string {
  if (locale === 'uk' && l.titleUk) return l.titleUk
  if (locale === 'ru' && l.titleRu) return l.titleRu
  return l.title
}

export type { LessonSummary }
